import { PreloadController, SeoController } from '@application/components';
import {
  generateItemsWithPromos,
  generateItemsWithPromosAmounts,
  isPromoItem,
  useModal,
  usePaginationSearch,
  useRouter,
} from '@application/utils';
import type { BreedListPageFormValues } from '@presentation/pages';
import { useGetStorePromos } from '@application/api';
import { BreedListPage } from '@presentation/pages';
import { FormikProvider, useFormik } from 'formik';
import hash from 'object-hash';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useBreedsWhereBuilder } from './api/useBreedWhereBuilder';
import { useFormatBreeds } from './api/useFormatBreeds';
import { useGetBreeds } from './api/useGetBreeds';
import { FORM_INITIAL_VALUES, PAGINATED_SIZE } from './constants';
import { useSearch } from './useSearch';
import { useSearchInput } from './useSearchInput';

export const BreedListPageController: FC = () => {
  const { translate } = useRouter();
  const { breeds, breedsLoading, breedsCount, breedsRefetch } = useGetBreeds();
  const { formattedBreeds, imagesToPreload } = useFormatBreeds(breeds);
  const { breedsWhereBuilder } = useBreedsWhereBuilder();
  const { currentPage, handlePaginationChange } = usePaginationSearch();
  const { setSearchFromValues, valuesFromSearch } = useSearch();

  const form = useFormik<BreedListPageFormValues>({
    enableReinitialize: true,
    initialValues: valuesFromSearch,
    onSubmit: (values) => {
      // defer execution like in BreederListPageController
      setTimeout(() => {
        setSearchFromValues(values);
      }, 0);

      breedsRefetch({ where: breedsWhereBuilder(values) });
    },
  });

  const searchInput = useSearchInput();

  const { modal: filtersModal, showFromClick: filtersShow } = useModal();

  const formValuesChanged = useMemo(() => {
    const hashInitialValues = hash(FORM_INITIAL_VALUES);
    const hashValues = hash(form.values);
    return hashValues !== hashInitialValues;
  }, [form.values]);

  const { listingPromoItems } = useGetStorePromos('breeds');
  const items = useMemo(() => {
    return generateItemsWithPromos(formattedBreeds ?? [], listingPromoItems, PAGINATED_SIZE);
  }, [formattedBreeds, listingPromoItems]);

  return (
    <FormikProvider value={form}>
      <PreloadController images={imagesToPreload} />
      <SeoController
        title={translate('breedList.seo.title')}
        description={translate('breedList.seo.description')}
        keywords={translate('breedList.seo.keywords')}
      />
      <BreedListPage
        loading={breedsLoading}
        items={items.list}
        isPromoItem={isPromoItem}
        searchInput={searchInput}
        filtersModal={{
          modal: filtersModal,
          onShowClick: filtersShow,
          loading: breedsLoading,
          canReset: formValuesChanged,
          onResetClick: () => {
            setSearchFromValues(FORM_INITIAL_VALUES);
            breedsRefetch({ where: breedsWhereBuilder(FORM_INITIAL_VALUES) });
          },
        }}
        pagination={{
          forcePage: currentPage,
          pageCount: breedsCount / generateItemsWithPromosAmounts(PAGINATED_SIZE).itemAmount,
          onChange: async (changedPage: number) => {
            handlePaginationChange(changedPage);
            await breedsRefetch({
              skip: generateItemsWithPromosAmounts(PAGINATED_SIZE).itemAmount * changedPage,
            });
          },
        }}
      />
    </FormikProvider>
  );
};
